/**
 * stageMotion — a tiny module-level store answering one question: is the
 * panel stage currently settled, or is its layout in motion?
 *
 * "In motion" covers the two ways slot geometry changes:
 *   - a layout transition: opening/closing a panel (or a width-override
 *     reset) reshuffles the remaining slots, which ease to their new
 *     right/width pixels over the 200ms CSS transition (panels.css). PanelStage
 *     reports these via `beginStageAnimation(durationMs)`.
 *   - a resize drag: the user is holding a splitter handle and slots track
 *     the pointer with no easing. PanelResizeHandle brackets the drag with
 *     `beginStageResize()` / `endStageResize()`.
 *
 * The Objects gallery uses the combined signal to freeze its expensive row
 * packing across both animations and manual drags. Most other consumers use
 * the panel-transition channel, which excludes manual resize so content can
 * reflow with the pointer (CSS reflow — PanelHost freezes React subtree
 * identity for the drag via the resize-only channel below). Consumers:
 *
 *   - Timeline, VideoPlayer metadata, and video_search — freeze for animated
 *     geometry changes, never for a drag;
 *   - objects_list — freezes for every stage-motion source;
 *   - PanelHost — reuses each panel's committed subtree and gives an easing
 *     body a compositor layer.
 *
 * Both animation channels are anchored at the commit that STARTS an ease
 * (PanelStage's geometry effect), not at the interaction that requested it:
 * a heavy panel can delay that commit well past the click, and a window
 * opened at click time would lift while slots were still moving.
 *
 * Plain module state (not React) so pointer-move and ResizeObserver
 * callbacks can read it without subscribing. React consumers use the matching
 * `useSyncExternalStore` binding for the channel they need.
 */

import { useSyncExternalStore } from 'react';

import type { Unsubscribe } from '@/types/common';

/** Edge notification: fires only on a true/false change, never redundantly. */
type IdleListener = (isIdle: boolean) => void;
type ResizeListener = (isResizing: boolean) => void;

// Margin past the CSS transition so the settle notification always lands
// after the browser has painted the final frame (transitions can end a
// frame or two late under load).
export const STAGE_ANIMATION_MARGIN_MS = 50;

let resizeDepth = 0; // nested beginStageResize() calls (defensive; 1 in practice)
let animationUntil = 0; // epoch ms until which a layout transition is considered live
let animationTimer: ReturnType<typeof setTimeout> | undefined;
let isStageIdleNow = true;
const listeners = new Set<IdleListener>();

// The resize channel alone: true while a pointer owns a splitter (panel,
// chat, or nav). PanelHost freezes panel subtree identity on this edge so a
// drag never re-renders panel content per ResizeObserver sample — the slots
// track the pointer, the content inside re-renders once on release.
let isResizeActive = false;
const resizeListeners = new Set<ResizeListener>();

function notifyResizeIfChanged(): void {
    const isResizingNow = resizeDepth > 0;
    if (isResizingNow === isResizeActive) {
        return;
    }
    isResizeActive = isResizingNow;
    for (const cb of resizeListeners) {
        cb(isResizeActive);
    }
}

// Panel bodies and most specialized consumers freeze only for geometry motion
// caused by panels entering/exiting. Manual resizing remains live for them.
let panelTransitionUntil = 0;
let panelTransitionTimer: ReturnType<typeof setTimeout> | undefined;
let isPanelTransitionOverNow = true;
const panelTransitionListeners = new Set<IdleListener>();

function computeIsStageIdle(): boolean {
    return resizeDepth === 0 && Date.now() >= animationUntil;
}

function notifyIfChanged(): void {
    const next = computeIsStageIdle();
    if (next === isStageIdleNow) {
        return;
    }
    isStageIdleNow = next;
    for (const cb of listeners) {
        cb(isStageIdleNow);
    }
}

function computeIsPanelTransitionOver(): boolean {
    return Date.now() >= panelTransitionUntil;
}

function notifyPanelTransitionIfChanged(): void {
    const next = computeIsPanelTransitionOver();
    if (next === isPanelTransitionOverNow) {
        return;
    }
    isPanelTransitionOverNow = next;
    for (const cb of panelTransitionListeners) {
        cb(isPanelTransitionOverNow);
    }
}

// A timer can run a millisecond before Date.now() reaches its delay. Then the edge waits for the
// rest of the time: no other timer would send it, and a frozen panel would stay frozen.
function startAnimationTimer(): void {
    clearTimeout(animationTimer);
    animationTimer = setTimeout(() => {
        if (Date.now() < animationUntil) {
            startAnimationTimer();
            return;
        }
        animationTimer = undefined;
        notifyIfChanged();
    }, animationUntil - Date.now());
}

function startPanelTransitionTimer(): void {
    clearTimeout(panelTransitionTimer);
    panelTransitionTimer = setTimeout(() => {
        if (Date.now() < panelTransitionUntil) {
            startPanelTransitionTimer();
            return;
        }
        panelTransitionTimer = undefined;
        notifyPanelTransitionIfChanged();
    }, panelTransitionUntil - Date.now());
}

function beginPanelTransition(durationMs: number): void {
    const until = Date.now() + durationMs;
    if (until <= panelTransitionUntil) {
        return;
    }
    panelTransitionUntil = until;
    startPanelTransitionTimer();
    notifyPanelTransitionIfChanged();
}

/**
 * A layout transition just started (or was extended): consider the stage in
 * motion for `durationMs` from now. Overlapping calls extend the deadline —
 * e.g. closing a second panel while the first close is still easing.
 */
export function beginStageAnimation(
    durationMs: number,
    { panelTransition: isPanelTransition = false }: { panelTransition?: boolean } = {},
): void {
    if (isPanelTransition) {
        beginPanelTransition(durationMs);
    }
    const until = Date.now() + durationMs;
    if (until <= animationUntil) {
        return; // already covered by a longer deadline
    }
    animationUntil = until;
    startAnimationTimer();
    notifyIfChanged();
}

/** Bracket a splitter drag. Every begin must be paired with an end. */
export function beginStageResize(): void {
    resizeDepth += 1;
    notifyIfChanged();
    notifyResizeIfChanged();
}

export function endStageResize(): void {
    resizeDepth = Math.max(0, resizeDepth - 1);
    notifyIfChanged();
    notifyResizeIfChanged();
}

/** Imperative read — safe from rAF / ResizeObserver / pointer callbacks. */
export function isStageIdle(): boolean {
    return computeIsStageIdle();
}

/**
 * Subscribe to idle-flag transitions. `cb(isIdle)` fires only on edges
 * (true → false / false → true), never redundantly. Returns unsubscribe.
 */
export function subscribeStageMotion(cb: IdleListener): Unsubscribe {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}

/** Imperative read for show/hide-driven panel geometry only. */
export function isPanelTransitionOver(): boolean {
    return computeIsPanelTransitionOver();
}

/** Subscribe to show/hide-driven transition edges only. */
export function subscribePanelTransition(cb: IdleListener): Unsubscribe {
    panelTransitionListeners.add(cb);
    return () => {
        panelTransitionListeners.delete(cb);
    };
}

/** React binding for panel-presence transitions, excluding manual resize. */
export function usePanelTransitionOver(): boolean {
    return useSyncExternalStore(subscribePanelTransition, isPanelTransitionOver);
}

/** Imperative read of the resize channel alone. */
export function isStageResizing(): boolean {
    return resizeDepth > 0;
}

/** Subscribe to drag start/end edges only. Returns unsubscribe. */
export function subscribeStageResize(cb: ResizeListener): Unsubscribe {
    resizeListeners.add(cb);
    return () => {
        resizeListeners.delete(cb);
    };
}

/** React binding — re-renders the consumer when a splitter drag starts/ends. */
export function useStageResizing(): boolean {
    return useSyncExternalStore(subscribeStageResize, isStageResizing);
}

/** Test hook — reset module state between cases. */
export function resetStageMotionForTests(): void {
    resizeDepth = 0;
    animationUntil = 0;
    clearTimeout(animationTimer);
    animationTimer = undefined;
    isStageIdleNow = true;
    listeners.clear();
    isResizeActive = false;
    resizeListeners.clear();
    panelTransitionUntil = 0;
    clearTimeout(panelTransitionTimer);
    panelTransitionTimer = undefined;
    isPanelTransitionOverNow = true;
    panelTransitionListeners.clear();
}
